import React, { useMemo, useState, useCallback } from "react";
import { useQuery } from "@apollo/client";
import Loading from "../../components/common/Loading";
import PaginatedFlatTable from "../../components/pagination/PaginatedFlatTable";
import { USER_SUBSCRIPTION_INVOICES } from "../../components/subscription/queries";
import { formattedDateTime } from "../../util/date";
import { GlobalStyles } from "../../constants/styles";
import useThemeColors from "../../components/organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import { useBranding } from "../../context/BrandingContext";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

export default function InvoicesPage() {
    const colors = useThemeColors();
    const { isOrgPortal } = useBranding();
    const { t } = useTranslation();

    const scopeId = "kbola-invoices-page";
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const pageSize = 12;

    const [invoices, setInvoices] = useState([]);

    const ui = useMemo(() => {
        return {
            page: {
                backgroundColor: GlobalStyles.colors.shark,
                fontFamily: "Poppins, sans-serif",
                minHeight: "100vh",
                padding: "28px 18px",
            },
            container: { maxWidth: 1200, margin: "0 auto" },

            hero: {
                borderRadius: 18,
                border: "1px solid rgba(255,255,255,0.08)",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                boxShadow: "0 18px 44px rgba(0,0,0,0.35)",
                padding: 18,
                marginBottom: 16,
                color: "#fff",
            },
            title: {
                margin: 0,
                color: colors.textOnPrimary,
                fontSize: 20,
                fontWeight: 900,
            },
            sub: {
                margin: "6px 0 0 0",
                color: colors.textOnPrimary,
                fontWeight: 700,
                fontSize: 13.5,
            },

            card: {
                borderRadius: 18,
                border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(0,0,0,0.18)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.30)",
                overflow: "hidden",
            },

            topActions: {
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginBottom: 10,
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(255,255,255,0.14)",
                background: "rgba(255,255,255,0.06)",
                color: "#ffffff",
                cursor: "pointer",
                lineHeight: 1,
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.12)",
                color: GlobalStyles.colors.woodsmoke,
            },

            pill: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.05)",
                color: "rgba(255,255,255,0.86)",
                fontWeight: 900,
                fontSize: 12,
                lineHeight: 1,
                whiteSpace: "nowrap",
            },
            pillPaid: {
                border: "1px solid rgba(25,135,84,0.28)",
                background: "rgba(25,135,84,0.12)",
                color: "#9ff0c6",
            },
            pillOpen: {
                border: "1px solid rgba(255,193,7,0.30)",
                background: "rgba(255,193,7,0.16)",
                color: "#ffd66b",
            },

            link: {
                color: GlobalStyles.colors.canary,
                textDecoration: "underline",
                fontWeight: 900,
            },
        };
    }, [colors.primary, colors.textOnPrimary]);

    const { loading, error, refetch } = useQuery(USER_SUBSCRIPTION_INVOICES, {
        variables: { page, pageSize },
        fetchPolicy: "cache-and-network",
        onCompleted: (resp) => {
            setInvoices(resp?.userSubscriptionInvoices?.objects || []);
            setTotalPages(resp?.userSubscriptionInvoices?.pages || 1);
        },
    });

    const onPageChange = useCallback((p) => setPage(p), []);

    if (loading) return <Loading />;

    if (error) {
        return (
            <div className="content-wrapper" id={scopeId} style={ui.page}>
                <div style={ui.container}>
                    <div className="alert alert-danger col-12 col-md-8 mx-auto text-center">
                        {getLocalizedGraphQLError(error, t, "errors.somethingWentWrong")}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="content-wrapper" id={scopeId} style={ui.page}>
            <div style={ui.container}>
                <section style={ui.hero}>
                    <h1 style={ui.title}>{t("invoicesPage.title")}</h1>
                    <p style={ui.sub}>
                        {t("invoicesPage.subtitle", { timezone })}
                    </p>
                </section>

                <div style={ui.topActions}>
                    <button
                        type="button"
                        style={{ ...ui.btn, ...ui.btnPrimary }}
                        onClick={() => refetch()}
                        title={t("invoicesPage.actions.refresh")}
                    >
                        <i className="fas fa-sync-alt" />
                        {t("invoicesPage.actions.refresh")}
                    </button>
                </div>

                <div style={ui.card}>
                    <PaginatedFlatTable
                        variant={isOrgPortal ? "light" : "dark"}
                        title={t("invoicesPage.table.title")}
                        page={page}
                        pages={totalPages}
                        onPageChange={onPageChange}
                        onAdd={null}
                        items={invoices}
                        loading={false}
                        headers={[
                            t("invoicesPage.table.headers.subscription"),
                            t("invoicesPage.table.headers.type"),
                            t("invoicesPage.table.headers.status"),
                            t("invoicesPage.table.headers.amountPaid"),
                            t("invoicesPage.table.headers.amountDue"),
                            t("invoicesPage.table.headers.period"),
                            "",
                        ]}
                        renderRow={(inv) => (
                            <tr key={inv.id}>
                                <td>{inv?.subscription?.plan?.name || "—"}</td>

                                <td>
                                    <span style={{ ...ui.pill, ...ui.pillOpen }}>
                                        {inv?.subscription?.plan?.application?.toUpperCase?.() || "—"}
                                    </span>
                                </td>

                                <td>
                                    <span
                                        style={{
                                            ...ui.pill,
                                            ...(inv.status === "paid" ? ui.pillPaid : ui.pillOpen),
                                        }}
                                    >
                                        <i
                                            className={`fas ${
                                                inv.status === "paid"
                                                    ? "fa-check-circle"
                                                    : "fa-exclamation-circle"
                                            }`}
                                        />
                                        {t(`invoicesPage.status.${inv.status}`)}
                                    </span>
                                </td>

                                <td>
                                    {inv.amountPaid != null
                                        ? `${(inv.amountPaid / 100).toFixed(2)} ${
                                            inv.currency?.toUpperCase?.() || ""
                                        }`
                                        : "—"}
                                </td>

                                <td>
                                    {inv.amountDue != null
                                        ? `${(inv.amountDue / 100).toFixed(2)} ${
                                            inv.currency?.toUpperCase?.() || ""
                                        }`
                                        : "—"}
                                </td>

                                <td>
                                    {inv.periodStart
                                        ? formattedDateTime(
                                            inv.periodStart,
                                            timezone,
                                            "en",
                                            "LLLL, yyyy"
                                        )
                                        : "—"}
                                </td>

                                <td>
                                    {inv.hostedInvoiceUrl ? (
                                        <a
                                            href={inv.hostedInvoiceUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={ui.link}
                                        >
                                            {t("invoicesPage.table.headers.view")}
                                        </a>
                                    ) : (
                                        "—"
                                    )}
                                </td>
                            </tr>
                        )}
                    />
                </div>

                <style>{`
          #${scopeId} button:focus,
          #${scopeId} a:focus,
          #${scopeId} .btn:focus {
            outline: none;
            box-shadow: 0 0 0 0.22rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
          }

          #${scopeId} table thead th{
            background: rgba(255,255,255,0.06);
            border-bottom: 1px solid rgba(255,255,255,0.08);
            color: rgba(255,255,255,0.74);
            font-size: 12px;
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 0.03em;
            padding: 12px 14px;
          }
          #${scopeId} table tbody td{
            border-top: 1px solid rgba(255,255,255,0.06);
            padding: 12px 14px;
            vertical-align: middle;
            color: rgba(255,255,255,0.86);
            font-weight: 700;
            font-size: 13.5px;
          }
          #${scopeId} table tbody tr:hover{
            background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.08);
          }
          #${scopeId} .table{
            background: transparent;
            color: rgba(255,255,255,0.86);
            margin-bottom: 0;
          }

          #${scopeId} i.fas,
          #${scopeId} i.far,
          #${scopeId} i.fal,
          #${scopeId} i.fa,
          #${scopeId} i[class^="fa-"],
          #${scopeId} i[class*=" fa-"] {
            font-family: "Font Awesome 5 Free" !important;
            font-weight: 900;
          }
          #${scopeId} i.fab {
            font-family: "Font Awesome 5 Brands" !important;
            font-weight: 400;
          }

          @media (max-width: 768px) {
            #${scopeId} { padding: 18px 12px !important; }
          }
        `}</style>
            </div>
        </div>
    );
}
